import { Breadcrumb } from 'antd';
import type { BreadcrumbProps } from 'antd';
import type { FC } from 'react';
import { Link, useLocation } from 'react-router';
import { navItems } from '@constants/routes/navItems';

// Human labels for the pages the sidebar lists, keyed by their one segment. ''
// is Home.
const PAGE_LABELS: Record<string, string> = Object.fromEntries(
	navItems.map(({ segment, label }) => [segment, label])
);

/**
 * Tabs and sidebar-less sub-pages are keyed by full path rather than segment:
 * they hang off a parent, and two parents can use the same segment — /apps/assign
 * and /users/assign are both 'assign' but mean different things.
 */
const NESTED_LABELS: Record<string, string> = Object.fromEntries(
	navItems.flatMap(({ path, tabs = [], pages = [] }) =>
		[...tabs, ...pages].map(({ segment, label }) => [`${path}/${segment}`, label])
	)
);

const Breadcrumbs: FC = () => {
	const { pathname } = useLocation();

	const segments = pathname.split('/').slice(1);
	// Always start from the root ("Home") crumb.
	const parts = segments[0] === '' ? [''] : ['', ...segments];

	const items: BreadcrumbProps['items'] = parts.map((segment, index) => {
		const isLast = index === parts.length - 1;
		const url = '/' + parts.slice(1, index + 1).join('/');
		// Full path first, so a shared segment resolves by where it sits.
		const label = NESTED_LABELS[url] ?? PAGE_LABELS[segment] ?? segment;

		return {
			key: index,
			title: isLast ? label : <Link to={url}>{label}</Link>,
		};
	});

	return <Breadcrumb items={items} />;
};

export default Breadcrumbs;
